<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <img
          class="logo auth-logo"
          alt="Continuum Design"
          src="/continuum-design-logo.svg"
        />
        <template v-if="$route.query.thanks">
          <h3 class="text-h5 font-weight-bold pb-4 text-center">Your account is now enabled for login.</h3>
          <p class="text-center">
            Contact us at
            <a href="mailto:support@continuumdesign.io">support@continuumdesign.io</a> with
            <br/>any questions, suggestions or feedback!
          </p>
        </template>
        <template v-else>
          <h3 class="text-title">Login to your account</h3>
        </template>
      </div>

      <!-- MFA Verification Screen -->
      <template v-if="mfaChallenge">
        <div class="auth-card-container" data-cy="mfa-challenge-screen">
          <h3 class="text-title">Two-Factor Authentication</h3>
          <p class="text-body-2 mb-4">Enter the 6-digit code from your authenticator app.</p>
        </div>
        <v-form ref="mfaForm" @submit.prevent="submitMfa">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              name="mfa-code"
              label="Authentication Code"
              v-model="mfaCode"
              :rules="[rules.required, rules.mfaCode]"
              maxlength="6"
              autofocus
              variant="outlined"
              density="default"
              prepend-inner-icon="mdi-shield-key"
              data-cy="mfa-code-input"
            ></v-text-field>

            <p class="message bg-error" v-if="error" data-cy="login-error">{{ error }}</p>
          </div>

          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part">
                <a class="text text-decoration-none" style="cursor: pointer" @click="cancelMfa">
                  Back to login
                </a>
              </div>
              <div class="auth-card-footer-part">
                <v-btn rounded="lg" size="large" color="primary" class="ml-auto d-flex" variant="flat" type="submit"
                       tabindex="1" data-cy="mfa-submit-btn">
                  Verify
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
      </template>

      <!-- Set New Password (invited users completing their first login) -->
      <template v-else-if="newPasswordChallenge">
        <div class="auth-card-container" data-cy="new-password-screen">
          <h3 class="text-title">Set your password</h3>
          <p class="text-body-2 mb-4">Welcome! Choose a password to finish setting up your account.</p>
        </div>
        <v-form ref="newPasswordForm" @submit.prevent="submitNewPassword">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              name="given-name"
              label="First Name"
              v-model="newFirstName"
              :rules="[rules.required]"
              autofocus
              variant="outlined"
              density="default"
              prepend-inner-icon="mdi-account-outline"
              autocomplete="given-name"
              data-cy="new-given-name-input"
            ></v-text-field>

            <v-text-field
              name="family-name"
              label="Last Name"
              v-model="newLastName"
              :rules="[rules.required]"
              variant="outlined"
              density="default"
              prepend-inner-icon="mdi-account-outline"
              autocomplete="family-name"
              data-cy="new-family-name-input"
            ></v-text-field>

            <v-text-field
              name="new-password"
              label="New Password"
              v-model="newPassword"
              :append-inner-icon="npShow ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              @click:append-inner="() => (npShow = !npShow)"
              :type="npShow ? 'text' : 'password'"
              :rules="[rules.required, rules.minLength]"
              counter
              variant="outlined"
              density="default"
              prepend-inner-icon="mdi-lock-outline"
              autocomplete="new-password"
              data-cy="new-password-input"
            ></v-text-field>

            <v-text-field
              name="confirm-new-password"
              label="Confirm Password"
              v-model="confirmNewPassword"
              :type="npShow ? 'text' : 'password'"
              :rules="[rules.required, rules.passwordsMatch]"
              variant="outlined"
              density="default"
              prepend-inner-icon="mdi-lock-check-outline"
              autocomplete="new-password"
              v-on:keyup.enter="submitNewPassword"
              data-cy="confirm-new-password-input"
            ></v-text-field>

            <p class="message bg-error" v-if="error" data-cy="login-error">{{ error }}</p>
          </div>

          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part">
                <a class="text text-decoration-none" style="cursor: pointer" @click="cancelNewPassword">
                  Back to login
                </a>
              </div>
              <div class="auth-card-footer-part">
                <v-btn rounded="lg" size="large" color="primary" class="ml-auto d-flex" variant="flat" type="submit"
                       :loading="newPasswordSubmitting" data-cy="new-password-submit-btn">
                  Set password &amp; sign in
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
      </template>

      <!-- Normal Login Form -->
      <template v-else>
        <v-form ref="form" v-model="valid">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              name="email"
              label="Email Address"
              type="email"
              ref="username"
              v-model="email"
              :rules="[rules.required, rules.email]"
              v-on:keyup.enter="submit"
              tabindex="1"
              autocomplete="username"
              variant="outlined"
              density="default"
              persistent-placeholder
              data-cy="email-input"
            ></v-text-field>

            <v-text-field
              name="password"
              label="Password"
              id="password"
              ref="password"
              v-model="password"
              :append-inner-icon="e1 ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              @click:append-inner="() => (e1 = !e1)"
              :type="e1 ? 'password' : 'text'"
              min="8"
              :rules="[rules.required]"
              counter
              v-on:keyup.enter="submit"
              tabindex="2"
              autocomplete="current-password"
              variant="outlined"
              density="default"
              persistent-placeholder
              data-cy="password-input"
            ></v-text-field>

            <v-alert v-if="$route.query.resetPasswordSuccess" type="success" variant="tonal" density="compact" class="mb-2">
              Password reset. You can now log in.
            </v-alert>
            <v-alert v-else-if="$route.query.reason === 'password_changed'" type="success" variant="tonal" density="compact" class="mb-2">
              Password changed. Please log in again. Other active sessions will expire within 5 minutes.
            </v-alert>
            <v-alert v-else-if="$route.query.reason === 'global_logout'" type="info" variant="tonal" density="compact" class="mb-2">
              All sessions revoked. Please log in again.
            </v-alert>
            <p v-else-if="$route.query.alreadyConfirmed" class="message bg-error">Account already verified.</p>

            <p class="message bg-error" v-if="error" data-cy="login-error">{{ error }}</p>
            <p class="message bg-indigo" v-if="message">{{ message }}</p>
          </div>

          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part auth-card-footer-part_column">
                <router-link :to="{ path: '/reset-password' }" class="text text-decoration-none">
                  Forgot password?
                </router-link>
                <router-link :to="{ path: '/confirm' }" class="text text-decoration-none">
                  Got a registration code?
                </router-link>
              </div>
              <div class="auth-card-footer-part">
                <p class="text">
                  No account?
                  <router-link
                    class="text text-decoration-none"
                    tabindex="5"
                    :to="{ path: '/signup', query: { projectId: $route.query.projectId || undefined, redirect: $route.query.redirect || undefined }  }"
                  >
                    <span class="light--text">Sign up</span>
                  </router-link>
                </p>
                <v-btn rounded="lg" size="large" color="primary" class="ml-auto d-flex" variant="flat" @click="submit"
                       tabindex="3">
                  Log in
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
      </template>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { authHelpers, parseAuthCustomState } from '@/api/authHelpers'
import { authProxyApi } from '@/api/authProxyApi'
import { Auth } from '@aws-amplify/auth'

export default {
  data () {
    return {
      valid: true,
      email: '',
      password: '',
      error: null,
      message: null,
      mfaChallenge: null,
      mfaCode: '',
      mfaSubmitting: false,
      newPasswordChallenge: null,
      newFirstName: '',
      newLastName: '',
      newPassword: '',
      confirmNewPassword: '',
      newPasswordSubmitting: false,
      npShow: false,
      rules: {
        required: (value) => !!value || 'Required.',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        },
        mfaCode: (value) => /^\d{6}$/.test(value) || 'Enter a 6-digit code.',
        minLength: (value) => (value || '').length >= 8 || 'At least 8 characters.',
        passwordsMatch: (value) => value === this.newPassword || 'Passwords do not match.'
      },
      e1: true
    }
  },
  created () {
    if (this.$route.query.username !== '') {
      this.email = this.$route.query.username
    }
  },
  async mounted () {
    if (this.$route.query.thanks) {
      // window.gtag('event', 'conversion', { send_to: 'AW-676520547/ZI1ACJashMwBEOPEy8IC' })
    }
    // Removed snackbars in favor of prominent static v-alert notices
    if (this.$route.query.username) {
      this.$refs.password.focus()
    }
    if (Auth && this.$route.query.googleSignIn) {
      try {
        const currentUser = await Auth.currentAuthenticatedUser()
        if (currentUser) {
          try {
            this.$loadingState.start()

            const sessionIdInfo = currentUser.signInUserSession.idToken.payload
            await this.initializeSession(sessionIdInfo)
          } catch (err) {
            this.$loadingState.stop()
            this.error = err.message.replace('GraphQL error: ', '')
            authHelpers.logoutAndClearStore()
          }
        }
      } catch (e) {
        console.log(e)
      }
    }
  },
  methods: {
    async updateUser () {
      const session = await this.$cognitoAuth.getSession()
      const sessionIdInfo = session.getIdToken().payload

      // Only the legacy GLHOMES SAML path needs the Amplify user object. Calling
      // Auth.currentAuthenticatedUser() unconditionally fails with "Failed to fetch"
      // now that browser identity-pool credentials are stripped (S3/file ops are
      // server-side), so scope it to the branch that actually uses it.
      if (sessionIdInfo?.identities?.[0]?.providerName === 'GLHOMES') {
        const currentUser = await Auth.currentAuthenticatedUser()
        await Auth.updateUserAttributes(currentUser, {
          'custom:organisation': 'GL Homes'
        })
      }

      const attributes = await this.$cognitoAuth.getUserAttributes()
      const indexCompany = attributes.findIndex(item => item.Name === 'custom:organisation')
      const company = indexCompany >= 0 ? attributes[indexCompany].Value : null
      const indexReferralSource = attributes.findIndex(item => item.Name === 'custom:referral_source')
      const referralSource = indexReferralSource >= 0 ? attributes[indexReferralSource].Value : null
      const indexUsageIntent = attributes.findIndex(item => item.Name === 'custom:usage_intent')
      const usageIntent = indexUsageIntent >= 0 ? attributes[indexUsageIntent].Value : null
      const indexFirstname = attributes.findIndex(item => item.Name === 'given_name')
      const firstname = indexFirstname >= 0 ? attributes[indexFirstname].Value : ''
      const indexLastname = attributes.findIndex(item => item.Name === 'family_name')
      const lastname = indexLastname >= 0 ? attributes[indexLastname].Value : ''
      const indexEmail = attributes.findIndex(item => item.Name === 'email')
      const email = indexEmail >= 0 ? attributes[indexEmail].Value : ''
      const indexPhoneNumber = attributes.findIndex(item => item.Name === 'phone_number')
      const phoneNumber = indexPhoneNumber >= 0 ? attributes[indexPhoneNumber].Value : null
      const queryResult = await store.dispatch('user/getCurrentUser')
      const variables = {
        firstname,
        lastname,
        email,
        company,
        referralSource,
        usageIntent,
        phoneNumber,
        increaseLoginCounter: true
      }
      if (queryResult.getCurrentUser) {
        await store.dispatch('user/updateUser', variables)
      } else {
        await store.dispatch('user/createUser', variables)
        window.trackSignupCompleted({ email })
        // Organisations are admin-provisioned: an admin creates the org and
        // assigns/creates the owner + members. New users do NOT self-create an
        // org at signup (createOrganisation is SuperAdmin-only). The `company`
        // they entered is still saved on their user record above for context.
      }
    },
    async submit () {
      const { valid } = await this.$refs.form.validate()
      if (valid) {
        try {
          this.$loadingState.start()
          const email = (this.email || '').toLowerCase()

          const res = await authProxyApi.login({
            email,
            password: this.password
          })

          // Unconfirmed account — route to /confirm with auto-resend so the
          // user can finish verification instead of being stuck on "Invalid
          // email or password" forever.
          if (res?.code === 'EMAIL_NOT_VERIFIED') {
            this.$loadingState.stop()
            this.$router.replace({
              path: '/confirm',
              query: {
                username: email,
                autoResend: true,
                projectId: this.$route.query.projectId || undefined,
                redirect: this.$route.query.redirect || undefined
              }
            })
            return
          }

          // MFA challenge returned - show MFA input screen
          if (res?.ok && res?.mfaRequired) {
            this.mfaChallenge = {
              session: res.session,
              email
            }
            this.error = null
            this.$loadingState.stop()
            return
          }

          // Invited user signing in with a temp password — show the set-new-password screen.
          if (res?.ok && res?.newPasswordRequired) {
            this.newPasswordChallenge = {
              session: res.session,
              email
            }
            this.newPassword = ''
            this.confirmNewPassword = ''
            this.error = null
            this.$loadingState.stop()
            return
          }

          if (!res?.ok || !res?.tokens?.idToken || !res?.tokens?.accessToken) {
            throw new Error(res?.message || 'Authentication failed. Please try again.')
          }

          this.$cognitoAuth.setSessionFromTokens(email, res.tokens)

          const session = await this.$cognitoAuth.getSession()
          const sessionIdInfo = session.getIdToken().payload

          await this.initializeSession(sessionIdInfo)
        } catch (err) {
          console.log(err)
          this.$loadingState.stop()
          this.error = err?.message || 'Authentication failed. Please try again.'
          authHelpers.logoutAndClearStore()
        }
      }
    },
    async submitMfa () {
      if (this.mfaSubmitting) return
      this.mfaSubmitting = true

      const { valid } = await this.$refs.mfaForm.validate()
      if (!valid) {
        this.mfaSubmitting = false
        return
      }

      try {
        this.$loadingState.start()
        this.error = null

        const res = await authProxyApi.mfaVerify({
          email: this.mfaChallenge.email,
          code: this.mfaCode,
          session: this.mfaChallenge.session
        })

        // Wrong code — stay on the MFA screen, clear the input, surface the
        // message. Without this branch the catch below would call
        // redirectToLoginForm() and drop the user back at the email/password
        // form, forcing them to re-enter their password from scratch.
        if (res?.code === 'MFA_CODE_INVALID') {
          this.$loadingState.stop()
          this.error = res.message
          this.mfaCode = ''
          return
        }

        if (!res?.ok || !res?.tokens?.idToken || !res?.tokens?.accessToken) {
          this.mfaCode = ''
          throw new Error(res?.message || 'Authentication failed. Please try again.')
        }

        this.$cognitoAuth.setSessionFromTokens(this.mfaChallenge.email, res.tokens)

        const session = await this.$cognitoAuth.getSession()
        const sessionIdInfo = session.getIdToken().payload

        await this.initializeSession(sessionIdInfo)
      } catch (err) {
        console.log(err)
        this.$loadingState.stop()
        this.error = err?.message || 'Authentication failed. Please try again.'
        this.redirectToLoginForm()
      } finally {
        this.mfaSubmitting = false
      }
    },
    redirectToLoginForm () {
      // Setting mfaChallenge to null hides the MFA <template v-if> and
      // shows the email/password <template v-else>, effectively "redirecting"
      // the user back to the login form on the same page.
      //
      // Also clear any pre-existing Cognito session in localStorage. Otherwise
      // a failed/cancelled MFA can leave a stale (still-valid) session from a
      // prior login intact, which the router would honor on the next navigation
      // — letting a wrong/expired TOTP code effectively "succeed" via the
      // lingering tokens.
      authHelpers.logoutAndClearStore()
      this.mfaChallenge = null
      this.mfaCode = ''
    },
    cancelMfa () {
      this.redirectToLoginForm()
      this.error = null
    },
    async submitNewPassword () {
      if (this.newPasswordSubmitting) return

      const { valid } = await this.$refs.newPasswordForm.validate()
      if (!valid) return

      this.newPasswordSubmitting = true
      try {
        this.$loadingState.start()
        this.error = null

        const res = await authProxyApi.newPassword({
          email: this.newPasswordChallenge.email,
          newPassword: this.newPassword,
          givenName: this.newFirstName,
          familyName: this.newLastName,
          session: this.newPasswordChallenge.session
        })

        // Stale/used challenge session — send them back to the login form.
        if (res?.code === 'CHALLENGE_EXPIRED') {
          authHelpers.logoutAndClearStore()
          this.newPasswordChallenge = null
          this.newPassword = ''
          this.confirmNewPassword = ''
          this.error = res.message
          this.$loadingState.stop()
          return
        }

        // Cognito chained an MFA challenge after the password was set.
        if (res?.ok && res?.mfaRequired) {
          this.mfaChallenge = { session: res.session, email: this.newPasswordChallenge.email }
          this.newPasswordChallenge = null
          this.newPassword = ''
          this.confirmNewPassword = ''
          this.error = null
          this.$loadingState.stop()
          return
        }

        // Password rejected by policy (or other soft failure) — stay on the
        // screen and show the reason instead of bouncing to the login form.
        if (res?.ok === false) {
          this.error = res?.message || 'Could not set your password. Please try again.'
          this.$loadingState.stop()
          return
        }

        if (!res?.tokens?.idToken || !res?.tokens?.accessToken) {
          throw new Error('Could not set your password. Please try again.')
        }

        this.$cognitoAuth.setSessionFromTokens(this.newPasswordChallenge.email, res.tokens)

        const session = await this.$cognitoAuth.getSession()
        const sessionIdInfo = session.getIdToken().payload

        await this.initializeSession(sessionIdInfo)
      } catch (err) {
        console.log(err)
        this.$loadingState.stop()
        this.error = err?.message || 'Could not set your password. Please try again.'
      } finally {
        this.newPasswordSubmitting = false
      }
    },
    cancelNewPassword () {
      authHelpers.logoutAndClearStore()
      this.newPasswordChallenge = null
      this.newFirstName = ''
      this.newLastName = ''
      this.newPassword = ''
      this.confirmNewPassword = ''
      this.error = null
    },
    async initializeSession (sessionIdInfo) {
      let adminUser = false
      let isSuperAdmin = false
      if (sessionIdInfo['cognito:groups']) {
        if (sessionIdInfo['cognito:groups'][0] === 'Admin') {
          adminUser = true
        }
        if (sessionIdInfo['cognito:groups'].includes('SuperAdmin')) {
          isSuperAdmin = true
        }
      }

      const state = parseAuthCustomState(this.$route.query.state)
      const projectId = this.$route.query.projectId || state?.projectId || null

      store.commit('login', { email: sessionIdInfo.email, adminUser, isSuperAdmin, userId: sessionIdInfo['cognito:username'] })
      await this.updateUser()
      this.$loadingState.stop()
      const redirect = this.$route.query.redirect || state?.redirect
      const path = redirect && redirect.startsWith('/') ? redirect : `/dashboard/${projectId || ''}`
      await this.$router.push({ path })
    }
  }
}
</script>

<style scoped>
.text {
  color: var(--text-dark);
  font-size: 13px;
  text-align: left;
  margin-bottom: 0;
  line-height: 14.3px;
}

.text-decoration-none {
  text-decoration: none;
}

.logo {
  width: 180px;
  margin-top: 9px;
  margin-bottom: 30px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
</style>
